import { useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import Sidebar from "./Sidebar";
import { Menu, AlertTriangle, X } from "lucide-react"; // Import Menu icon
import { MindConnectMark, MindConnectWordmark, MindConnectTagline } from "../components/ui/MindConnectLogo";
import NotificationBell from "../components/NotificationBell";
import FollowUpBell from "../components/FollowUpBell";
import AlertsBell from "../components/AlertsBell";
import CrisisRibbon from "../components/CrisisRibbon";
import SupervisionRibbon from "../components/SupervisionRibbon";
import HelpButton from "../components/help/HelpButton";
import NewMessageRibbon from "../components/NewMessageRibbon";
import PatientWaitingPopup from "../components/PatientWaitingPopup";
import DesktopAlertsPrompt from "../components/DesktopAlertsPrompt";
import { playNotificationTone } from "../utils/notificationSound";
import api from "../api";
import { refreshPulse, subscribePulse } from "../utils/pulse";

// How long a closed expiry banner stays hidden before it's shown again -
// long enough not to nag on every page nav, short enough that it still
// comes back the same day for someone actually ignoring it. Keyed per user
// (not just a flat key) - otherwise one user dismissing it on a shared
// browser would silently suppress it for the next person who logs in on
// the same machine, who may not have seen it at all yet.
const BANNER_SNOOZE_MS = 12 * 60 * 60 * 1000;
const BANNER_SNOOZE_KEY_PREFIX = "subscriptionBannerSnoozedUntil";
// "Add your payout bank account" nudge - lower urgency, so a day's snooze.
const BANK_BANNER_SNOOZE_MS = 24 * 60 * 60 * 1000;
const BANK_BANNER_SNOOZE_KEY_PREFIX = "bankBannerSnoozedUntil";

// "Good morning, Jaya" plus today's date, for the desktop top bar.
function TopbarGreeting() {
  let name = "";
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    name = (auth.user_name || auth.name || "").trim().split(/\s+/)[0] || "";
  } catch { /* no saved login */ }
  const now = new Date();
  const h = now.getHours();
  const part = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  return (
    <div className="min-w-0">
      <p className="text-base font-semibold text-secondary-900 truncate">{part}{name ? `, ${name}` : ""}</p>
      <p className="text-xs text-secondary-500">{now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
    </div>
  );
}

export default function DashboardLayout({ children, hideSidebar = false, startCollapsed = false }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(startCollapsed);
  const [messageSummary, setMessageSummary] = useState({ total_unread: 0, threads: [] });
  const [followUpSummary, setFollowUpSummary] = useState({ due_count: 0, items: [] });
  // Staff alerts (notifications/notify.py): crisis alerts, reviews, reminders.
  const [alerts, setAlerts] = useState({ items: [], unread_count: 0 });
  const lastSeenUrgent = useRef(null);
  // After the user reads or dismisses an alert: poll now, not in 30s.
  const refreshAlerts = () => refreshPulse();
  const [ribbonMessage, setRibbonMessage] = useState(null);
  const [subscriptionWarning, setSubscriptionWarning] = useState(null);
  const [bannerSnoozed, setBannerSnoozed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // Strictly the org's Admin - the only role that can save bank details.
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [getsPatientAlerts, setGetsPatientAlerts] = useState(false);
  const [bankWarning, setBankWarning] = useState(null);
  const [bankBannerSnoozed, setBankBannerSnoozed] = useState(false);
  const bankSnoozeKeyRef = useRef(BANK_BANNER_SNOOZE_KEY_PREFIX);
  // null means "haven't fetched yet" - distinct from 0, so a page that
  // already had unread messages before this mount never fires the ribbon
  // on its very first poll, only on a genuine increase after that.
  const lastSeenTotal = useRef(null);
  // Resolved once the JWT is decoded (see the expiry-banner effect below) -
  // falls back to the flat prefix if a token somehow can't be read, so the
  // banner still works, just without per-user scoping in that edge case.
  const snoozeKeyRef = useRef(BANNER_SNOOZE_KEY_PREFIX);
  // The banner lives outside the scrollable <main> (see below) specifically
  // so it's always visible - but the sidebar and notification bells are
  // `position: fixed`, which means being a later sibling in the DOM does
  // NOT push them down the way normal flow would; they have to be told the
  // banner's real height explicitly, or it renders on top of them instead
  // of above them. Measured via ResizeObserver (not a hardcoded height)
  // since the banner's text can wrap to two lines on a narrow viewport.
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const dismissSubscriptionBanner = () => {
    try {
      localStorage.setItem(snoozeKeyRef.current, String(Date.now() + BANNER_SNOOZE_MS));
    } catch (err) {
      // localStorage unavailable (private mode, etc.) - banner just won't
      // stay snoozed across reloads, not worth failing the close action over.
    }
    setBannerSnoozed(true);
  };

  const dismissBankBanner = () => {
    try {
      localStorage.setItem(bankSnoozeKeyRef.current, String(Date.now() + BANK_BANNER_SNOOZE_MS));
    } catch (err) {
      // see dismissSubscriptionBanner
    }
    setBankBannerSnoozed(true);
  };

  // The header's bells all come from the shared background poll
  // (utils/pulse.js -> GET /pulse). The tones fire here, once per change.
  useEffect(() => {
    if (hideSidebar) return undefined;
    return subscribePulse((p) => {
      if (p.messages) {
        const total = p.messages.total_unread || 0;
        if (lastSeenTotal.current !== null && total > lastSeenTotal.current) {
          const latest = p.messages.threads?.[0];
          setRibbonMessage(latest?.patient_name ? `New message from ${latest.patient_name}` : "You have a new message");
          playNotificationTone();
        }
        lastSeenTotal.current = total;
        setMessageSummary(p.messages);
      }
      if (p.follow_ups) setFollowUpSummary(p.follow_ups);
      if (p.notifications) {
        const urgent = (p.notifications.items || []).filter((n) => !n.read && n.severity === "urgent").length;
        // A new urgent alert plays the same tone as a new patient message.
        if (lastSeenUrgent.current !== null && urgent > lastSeenUrgent.current) playNotificationTone();
        lastSeenUrgent.current = urgent;
        setAlerts(p.notifications);
      }
    });
  }, [hideSidebar]);

  // Expiry warning banner - the backend already computes the 7-day
  // (monthly) / 15-day (yearly) threshold and whether the org is locked
  // (show_expiry_warning/is_locked on GET /organizations/subscription) -
  // this never re-derives that rule itself. A once-per-mount fetch is
  // enough; expiry status doesn't change minute to minute. If the org is
  // already locked, api.js's response interceptor will have redirected to
  // /subscription-locked before this page is even reachable, so there's
  // nothing to additionally handle for that case here.
  useEffect(() => {
    if (hideSidebar) return;
    try {
      const storedUser = localStorage.getItem("auth");
      if (storedUser) {
        const authData = JSON.parse(storedUser);
        if (authData.token) {
          const decoded = jwtDecode(authData.token);
          const role = decoded.sub?.role?.toLowerCase() || authData.role?.toLowerCase();
          setIsAdmin(["admin", "ops", "operations"].includes(role));
          // Doctors (and solo practitioners, who log in as Admin) get "patient
          // waiting" alerts, so they're offered system notifications.
          setGetsPatientAlerts(["doctor", "admin"].includes(role));
          setIsOrgAdmin(role === "admin");

          const userId = decoded.sub?.user_id || authData.user_id;
          if (userId) {
            snoozeKeyRef.current = `${BANNER_SNOOZE_KEY_PREFIX}_${userId}`;
            bankSnoozeKeyRef.current = `${BANK_BANNER_SNOOZE_KEY_PREFIX}_${userId}`;
          }
        }
      }
    } catch (err) {
      console.error("DashboardLayout: error decoding token:", err);
    }
    try {
      // Login.js sets this flag right after a successful login (any of its
      // three success paths), before the very first post-login navigate().
      // A fresh login always forces the banner to show at least once, even
      // if this same user had snoozed it earlier in a previous session -
      // "I dismissed it yesterday" shouldn't mean "never warn me again,"
      // only "not again in the next 12 hours of that one session."
      const forcedThisLogin = localStorage.getItem("forceSubscriptionBannerOnce") === "true";
      if (forcedThisLogin) {
        localStorage.removeItem("forceSubscriptionBannerOnce");
        setBannerSnoozed(false);
        setBankBannerSnoozed(false);
      } else {
        const snoozedUntil = Number(localStorage.getItem(snoozeKeyRef.current) || 0);
        setBannerSnoozed(snoozedUntil > Date.now());
        const bankSnoozedUntil = Number(localStorage.getItem(bankSnoozeKeyRef.current) || 0);
        setBankBannerSnoozed(bankSnoozedUntil > Date.now());
      }
    } catch (err) {
      // ignore - just show the banner as if never snoozed
    }
    api.fetchSubscription()
      .then((res) => {
        const data = res.data || {};
        if (data.has_subscription && data.show_expiry_warning) {
          setSubscriptionWarning(data);
        }
        if (data.payout_bank_missing) {
          setBankWarning({ held: !!data.online_payment_held });
        }
      })
      .catch(() => {});
  }, [hideSidebar]);

  const showSubscriptionBanner = subscriptionWarning && !bannerSnoozed;
  // One banner at a time - the subscription expiry takes priority.
  const showBankBanner = !showSubscriptionBanner && bankWarning && isOrgAdmin && !bankBannerSnoozed;
  const showBanner = showSubscriptionBanner || showBankBanner;

  useEffect(() => {
    if (!showBanner || !bannerRef.current) {
      setBannerHeight(0);
      return;
    }
    const el = bannerRef.current;
    const update = () => setBannerHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showBanner, showSubscriptionBanner]);

  return (
    <div className="flex flex-col h-screen bg-transparent overflow-x-hidden font-sans text-secondary-900">
      {/* Expiry alert - deliberately outside the scrollable <main> below (not
          just `position: sticky` on it) so it's always visible regardless of
          scroll position or which page is open, full-bleed across the
          sidebar too so it reads as urgent rather than a routine notice. */}
      {showBankBanner && (
        <div ref={bannerRef} className="relative z-50 flex items-center justify-center gap-3 bg-amber-500 px-10 py-3 text-white shadow-lg">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span className="text-sm font-semibold text-center">
            {bankWarning.held
              ? "Your patients can't pay you online until you add a payout bank account."
              : "Add a payout bank account so your online payments can be paid out to you."}
          </span>
          <a
            href="/settings?tab=General"
            className="whitespace-nowrap rounded-lg bg-white px-3 py-1.5 text-sm font-bold text-amber-700 hover:bg-amber-50 transition-colors"
          >
            Add Bank Details
          </a>
          <button
            type="button"
            onClick={dismissBankBanner}
            className="absolute right-3 p-1 rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-colors"
            aria-label="Remind me tomorrow"
            title="Remind me tomorrow"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {showSubscriptionBanner && (
        <div ref={bannerRef} className="relative z-50 flex items-center justify-center gap-3 bg-gradient-to-r from-red-600 via-red-600 to-orange-500 px-4 py-3 text-white shadow-lg">
          <AlertTriangle className="h-5 w-5 shrink-0 animate-pulse" />
          <span className="text-sm font-semibold text-center">
            Your subscription ends on {subscriptionWarning.end_date} ({subscriptionWarning.days_remaining} day
            {subscriptionWarning.days_remaining === 1 ? "" : "s"} left).{" "}
            {isAdmin ? "Renew now to avoid losing access." : "Contact your organization admin to renew."}
          </span>
          {isAdmin && (
            <a
              href="/finance?tab=Subscription"
              className="whitespace-nowrap rounded-lg bg-white px-3 py-1.5 text-sm font-bold text-red-700 hover:bg-red-50 transition-colors"
            >
              Pay Now
            </a>
          )}
          <button
            type="button"
            onClick={dismissSubscriptionBanner}
            className="absolute right-3 p-1 rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-colors"
            aria-label="Dismiss for 12 hours"
            title="Dismiss for 12 hours"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex flex-1 min-h-0 overflow-x-hidden">
        <PatientWaitingPopup alerts={alerts} onChange={refreshAlerts} />
        {getsPatientAlerts && !hideSidebar && <DesktopAlertsPrompt />}
        {ribbonMessage && (
          <NewMessageRibbon message={ribbonMessage} onDone={() => setRibbonMessage(null)} />
        )}

        {/* Sidebar */}
        {!hideSidebar && (
          <Sidebar
            isOpen={sidebarOpen}
            onClose={closeSidebar}
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleCollapse}
            topOffset={bannerHeight}
          />
        )}

        {/* Overlay for mobile when sidebar is open */}
        {sidebarOpen && !hideSidebar && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
            onClick={closeSidebar}
          />
        )}

        {/* Main Content */}
        <div className={`flex-1 flex flex-col ${hideSidebar ? '' : sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} min-w-0 overflow-x-hidden relative z-0 transition-all duration-300`}>
          {/* Mobile top bar - Only visible on mobile */}
          {!hideSidebar && (
            <div className="md:hidden flex items-center justify-between px-4 h-14 flex-shrink-0 backdrop-blur-xl bg-white/80 border-b border-secondary-100 sticky top-0 z-30">
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-xl text-secondary-600 hover:bg-secondary-100 transition-all"
                aria-label="Toggle menu"
              >
                <Menu size={20} />
              </button>
              <div className="flex items-center gap-2">
                <MindConnectMark className="w-7 h-7" />
                <div className="leading-tight">
                  <MindConnectWordmark className="text-base" />
                  <MindConnectTagline className="text-[7px]" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <CrisisRibbon compact />
                <SupervisionRibbon compact />
                <HelpButton compact />
                <AlertsBell alerts={alerts} onChange={refreshAlerts} />
                <FollowUpBell followUps={followUpSummary} />
                <NotificationBell summary={messageSummary} />
              </div>
            </div>
          )}

          {/* Desktop top bar: greeting and date on the left, alerts on the right.
              It sits above the scrolling <main>, so it stays put and page
              buttons never run under the bells. */}
          {!hideSidebar && (
            <header className="hidden md:flex relative z-30 items-center justify-between gap-4 h-16 px-8 flex-shrink-0 border-b border-secondary-100/80 bg-white/60 backdrop-blur-xl">
              <TopbarGreeting />
              <div className="flex items-center gap-1">
                <span className="mr-2"><CrisisRibbon /></span>
                <span className="mr-2"><SupervisionRibbon /></span>
                <HelpButton />
                <AlertsBell alerts={alerts} onChange={refreshAlerts} />
                <FollowUpBell followUps={followUpSummary} />
                <NotificationBell summary={messageSummary} />
              </div>
            </header>
          )}

          <main className="flex-1 p-4 md:p-8 md:pt-6 overflow-y-auto overflow-x-hidden bg-transparent">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
